/**
 * @engine/input — Gamepad Device
 *
 * Gamepad input device (Xbox/PS layout).
 */

import type { GamepadButton as GamepadButtonName, GamepadAxis } from '../types';
import { InputDevice } from './InputDevice';
import { ButtonControl } from '../controls/ButtonControl';
import { AxisControl } from '../controls/AxisControl';
import { Vector2Control } from '../controls/Vector2Control';

/**
 * Standard gamepad device (Xbox/PS layout).
 */
export class Gamepad extends InputDevice {
  // Face buttons
  readonly a: ButtonControl;
  readonly b: ButtonControl;
  readonly x: ButtonControl;
  readonly y: ButtonControl;

  // Shoulders
  readonly leftBumper: ButtonControl;
  readonly rightBumper: ButtonControl;

  // Triggers (analog buttons)
  readonly leftTrigger: ButtonControl;
  readonly rightTrigger: ButtonControl;
  readonly leftTriggerAxis: AxisControl;
  readonly rightTriggerAxis: AxisControl;

  // Meta
  readonly select: ButtonControl;
  readonly start: ButtonControl;
  readonly home: ButtonControl;

  // Sticks
  readonly leftStickButton: ButtonControl;
  readonly rightStickButton: ButtonControl;
  readonly leftStick: Vector2Control;
  readonly rightStick: Vector2Control;

  // D-pad (as buttons and as vector)
  readonly dpadUp: ButtonControl;
  readonly dpadDown: ButtonControl;
  readonly dpadLeft: ButtonControl;
  readonly dpadRight: ButtonControl;
  readonly dpad: Vector2Control;

  // Optional
  readonly touchpad: ButtonControl;

  constructor(index: number = 0, name: string = 'Gamepad', vendorId?: number, productId?: number) {
    super('gamepad', index, name, vendorId, productId);

    // Face buttons
    this.a = new ButtonControl('a');
    this.b = new ButtonControl('b');
    this.x = new ButtonControl('x');
    this.y = new ButtonControl('y');

    // Shoulders
    this.leftBumper = new ButtonControl('leftBumper');
    this.rightBumper = new ButtonControl('rightBumper');

    // Triggers (both button and axis representation)
    this.leftTrigger = new ButtonControl('leftTrigger', 0.1);
    this.rightTrigger = new ButtonControl('rightTrigger', 0.1);
    this.leftTriggerAxis = new AxisControl('leftTriggerAxis', false); // 0..1
    this.rightTriggerAxis = new AxisControl('rightTriggerAxis', false); // 0..1

    // Meta
    this.select = new ButtonControl('select');
    this.start = new ButtonControl('start');
    this.home = new ButtonControl('home');

    // Stick buttons
    this.leftStickButton = new ButtonControl('leftStickButton');
    this.rightStickButton = new ButtonControl('rightStickButton');

    // Sticks
    this.leftStick = new Vector2Control('leftStick');
    this.rightStick = new Vector2Control('rightStick');

    // D-pad
    this.dpadUp = new ButtonControl('dpadUp');
    this.dpadDown = new ButtonControl('dpadDown');
    this.dpadLeft = new ButtonControl('dpadLeft');
    this.dpadRight = new ButtonControl('dpadRight');
    this.dpad = new Vector2Control('dpad');

    // Optional
    this.touchpad = new ButtonControl('touchpad');

    // Register all controls
    this.registerControl(this.a);
    this.registerControl(this.b);
    this.registerControl(this.x);
    this.registerControl(this.y);
    this.registerControl(this.leftBumper);
    this.registerControl(this.rightBumper);
    this.registerControl(this.leftTrigger);
    this.registerControl(this.rightTrigger);
    this.registerControl(this.leftTriggerAxis);
    this.registerControl(this.rightTriggerAxis);
    this.registerControl(this.select);
    this.registerControl(this.start);
    this.registerControl(this.home);
    this.registerControl(this.leftStickButton);
    this.registerControl(this.rightStickButton);
    this.registerControl(this.leftStick);
    this.registerControl(this.rightStick);
    this.registerControl(this.dpadUp);
    this.registerControl(this.dpadDown);
    this.registerControl(this.dpadLeft);
    this.registerControl(this.dpadRight);
    this.registerControl(this.dpad);
    this.registerControl(this.touchpad);
  }

  // ============================================================================
  // State Updates (from W3C Gamepad API indices)
  // ============================================================================

  /**
   * Update from W3C Gamepad API button array.
   * Standard mapping: https://w3c.github.io/gamepad/#remapping
   */
  updateFromGamepadAPI(buttons: readonly globalThis.GamepadButton[], axes: readonly number[], time: number): void {
    // Buttons (W3C standard mapping)
    if (buttons.length > 0) this.setButton('a', buttons[0], time);
    if (buttons.length > 1) this.setButton('b', buttons[1], time);
    if (buttons.length > 2) this.setButton('x', buttons[2], time);
    if (buttons.length > 3) this.setButton('y', buttons[3], time);
    if (buttons.length > 4) this.setButton('leftBumper', buttons[4], time);
    if (buttons.length > 5) this.setButton('rightBumper', buttons[5], time);
    if (buttons.length > 6) this.setTrigger('leftTrigger', buttons[6], time);
    if (buttons.length > 7) this.setTrigger('rightTrigger', buttons[7], time);
    if (buttons.length > 8) this.setButton('select', buttons[8], time);
    if (buttons.length > 9) this.setButton('start', buttons[9], time);
    if (buttons.length > 10) this.setButton('leftStickButton', buttons[10], time);
    if (buttons.length > 11) this.setButton('rightStickButton', buttons[11], time);
    if (buttons.length > 12) this.setButton('dpadUp', buttons[12], time);
    if (buttons.length > 13) this.setButton('dpadDown', buttons[13], time);
    if (buttons.length > 14) this.setButton('dpadLeft', buttons[14], time);
    if (buttons.length > 15) this.setButton('dpadRight', buttons[15], time);
    if (buttons.length > 16) this.setButton('home', buttons[16], time);
    if (buttons.length > 17) this.setButton('touchpad', buttons[17], time);

    // Axes (W3C standard mapping)
    if (axes.length >= 2) {
      this.leftStick.update({ x: axes[0], y: axes[1] }, time);
    }
    if (axes.length >= 4) {
      this.rightStick.update({ x: axes[2], y: axes[3] }, time);
    }

    // Update dpad vector from buttons
    this.updateDpadVector(time);
  }

  /**
   * Set button state (from button value or pressed boolean)
   */
  private setButton(name: GamepadButtonName, value: globalThis.GamepadButton | number | boolean, time: number): void {
    const control = this.controls.get(name) as ButtonControl | undefined;
    if (!control) return;

    if (typeof value === 'boolean') {
      control.update(value, time);
    } else if (typeof value === 'number') {
      control.setAnalogValue(value, time);
    } else {
      // GamepadButton object (W3C API)
      const btn = value as unknown as { pressed: boolean; value: number };
      control.setAnalogValue(btn.value ?? (btn.pressed ? 1 : 0), time);
    }
  }

  /**
   * Set trigger state (updates both button and axis)
   */
  private setTrigger(
    name: 'leftTrigger' | 'rightTrigger',
    value: globalThis.GamepadButton | number,
    time: number
  ): void {
    const axisControl = name === 'leftTrigger' ? this.leftTriggerAxis : this.rightTriggerAxis;
    const buttonControl = name === 'leftTrigger' ? this.leftTrigger : this.rightTrigger;

    let axisValue: number;
    if (typeof value === 'number') {
      axisValue = value;
    } else {
      const btn = value as unknown as { value: number; pressed: boolean };
      axisValue = btn.value ?? (btn.pressed ? 1 : 0);
    }

    axisControl.update(axisValue, time);
    buttonControl.setAnalogValue(axisValue, time);
  }

  /**
   * Update dpad vector from individual buttons
   */
  private updateDpadVector(time: number): void {
    let x = 0;
    let y = 0;
    if (this.dpadRight.isPressed) x += 1;
    if (this.dpadLeft.isPressed) x -= 1;
    if (this.dpadUp.isPressed) y += 1;
    if (this.dpadDown.isPressed) y -= 1;
    this.dpad.update({ x, y }, time);
  }

  // ============================================================================
  // Direct Setters (for non-standard backends)
  // ============================================================================

  setButtonState(name: GamepadButtonName, pressed: boolean, time: number): void {
    this.setButton(name, pressed, time);
  }

  setStick(name: 'leftStick' | 'rightStick', x: number, y: number, time: number): void {
    const stick = name === 'leftStick' ? this.leftStick : this.rightStick;
    stick.update({ x, y }, time);
  }

  setTriggerValue(name: 'leftTrigger' | 'rightTrigger', value: number, time: number): void {
    this.setTrigger(name, value, time);
  }

  // ============================================================================
  // Control Resolution
  // ============================================================================

  override getControl(name: string): ButtonControl | AxisControl | Vector2Control | undefined {
    // Check registered controls
    const control = super.getControl(name);
    if (control) return control as ButtonControl | AxisControl | Vector2Control;

    // Aliases
    switch (name) {
      // PS aliases
      case 'cross':
        return this.a;
      case 'circle':
        return this.b;
      case 'square':
        return this.x;
      case 'triangle':
        return this.y;
      case 'l1':
        return this.leftBumper;
      case 'r1':
        return this.rightBumper;
      case 'l2':
        return this.leftTrigger;
      case 'r2':
        return this.rightTrigger;
      case 'l3':
        return this.leftStickButton;
      case 'r3':
        return this.rightStickButton;
      case 'options':
        return this.start;
      case 'share':
        return this.select;
      // Axis aliases
      case 'leftStickX':
        return this.leftStick.x;
      case 'leftStickY':
        return this.leftStick.y;
      case 'rightStickX':
        return this.rightStick.x;
      case 'rightStickY':
        return this.rightStick.y;
      default:
        return undefined;
    }
  }
}
